import React from "react";
import { Phone, MessageCircle } from "lucide-react";

// ContactActions — click-to-call + WhatsApp deep-link with a pre-composed
// arrival-day greeting that already carries the check-in time. Turns the
// arrivals table into a one-tap CRM: the host doesn't have to draft the same
// message for every guest. Falls back to a "Nessun telefono" chip when the
// booking has no phone on file (should be rare now that phone is mandatory).
// Extracted from ReceptionTab.jsx (Iteration 62 — modularisation).
export default function ContactActions({ booking, IT, checkinTime }) {
  const phone = (booking.guest_phone || "").trim();
  if (!phone) {
    return (
      <span className="text-[10px] italic text-muted-foreground" data-testid={`contact-none-${booking.id}`}>
        {IT ? "Nessun telefono" : "No phone"}
      </span>
    );
  }
  // WhatsApp expects the number as digits only, keeping the leading country code.
  const waNumber = phone.replace(/[^\d]/g, "");
  const firstName = (booking.guest_name || "").split(" ")[0] || (IT ? "Ospite" : "Guest");
  const roomLabel = booking.room_name ? ` in ${booking.room_name}` : "";
  const timeSuffix = checkinTime ? (IT ? ` dalle ore ${checkinTime}` : ` from ${checkinTime}`) : "";
  // Magic-link deep link → Guest Portal ETA card. When present, we let the
  // guest reply with a single tap instead of typing an ETA in chat; their
  // submission on that page triggers a push notification on the host's
  // dashboard via /guest-portal/{sid}/eta (Iteration 64).
  const etaLink = booking.session_id
    ? `${window.location.origin}/guest/portal/${booking.session_id}`
    : "";
  const etaLine = etaLink
    ? (IT
        ? `\n\n👉 Rispondi con un tap qui: ${etaLink}`
        : `\n\n👉 Reply with one tap here: ${etaLink}`)
    : "";
  const msg = IT
    ? `Ciao ${firstName}! 👋 Ti aspettiamo oggi${roomLabel}${timeSuffix}.\n\n⚠️ Per organizzare al meglio la tua accoglienza, comunicaci con precisione l'orario previsto di arrivo (anche approssimativo). È fondamentale per garantirti il servizio migliore.${etaLine}\n\nA presto! 🙌`
    : `Hi ${firstName}! 👋 We're expecting you today${roomLabel}${timeSuffix}.\n\n⚠️ To arrange the best welcome, please tell us your expected arrival time (even approximate). This is essential so we can be ready for you.${etaLine}\n\nSee you soon! 🙌`;
  const waUrl = `https://wa.me/${waNumber}?text=${encodeURIComponent(msg)}`;
  return (
    <div className="flex items-center gap-1.5">
      <a
        href={`tel:${phone}`}
        className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-border text-primary hover:bg-primary hover:text-primary-foreground transition-all"
        title={IT ? `Chiama ${phone}` : `Call ${phone}`}
        data-testid={`contact-call-${booking.id}`}
      >
        <Phone className="w-3.5 h-3.5"/>
      </a>
      <a
        href={waUrl}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center justify-center w-8 h-8 rounded-full text-white bg-[#25D366] hover:bg-[#128C7E] transition-all"
        title={IT ? "Apri WhatsApp con messaggio pre-compilato" : "Open WhatsApp with pre-filled message"}
        data-testid={`contact-wa-${booking.id}`}
      >
        <MessageCircle className="w-3.5 h-3.5"/>
      </a>
    </div>
  );
}
